/**
 * Cart page custom behaviours (Domaine des Hautes Glaces)
 *
 * Since the downgrade from Shopify Plus to Shopify Advanced the checkout can no
 * longer host UI extensions, so the following features live on the cart page:
 *  - Gift message (cart attributes `gift_message` + `include_gift_message`, same keys
 *    as the former checkout extension). The message is saved through the Ajax API only:
 *    the message carries the literal "\r\n" text required by the logistics partner for every
 *    line break (real CR+LF characters are normalised to LF by Shopify's checkout). It counts in the max length.
 *  - Terms & conditions / legal age acceptance gating the "Procéder au paiement" button
 *    (cart attribute `agree_to_terms`)
 *  - Cart note character counter (unchanged)
 *  - Quantity +/- handler keeping the excise/vignette tax attributes in sync (unchanged)
 *
 * The cart form (#cartFormSubmit) is submitted natively so every hidden attribute input
 * reaches the checkout as before. custom-script.js runs the MinMaxify rules on the same
 * submit event (bubbling phase); the guards below run first (capture phase).
 */
(function () {
  'use strict';

  var FORBIDDEN_CHARS = ['"', '_', '=', '+', '°', '~', '{', '}', '@', '^', '`', '<', '>', ':', ';', '/', '§', '¨', '£', '¤', '%', 'µ', '|', '[', ']', '&', '#'];
  var EMOJI_REGEX = /(?:[✀-➿]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[#-9]️?⃣|㊙|㊗|〽|〰|Ⓜ|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|🆎|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|🈚|🈯|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|‼|⁉|[▪-▫]|▶|◀|[◻-◾]|©|®|™|ℹ|🀄|[☀-⛿]|⬅|⬆|⬇|⬛|⬜|⭐|⭕|⌚|⌛|⌨|⏏|[⏩-⏳]|[⏸-⏺]|🃏|⤴|⤵|[←-⇿])/g;

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  // Logistics (Veolog) prints the card from the attribute and expects the literal text "\r\n"
  // (backslash r, backslash n: 4 characters) for every line break. Real CR+LF characters are not
  // an option: Shopify's checkout normalises them to LF. The sequence counts in the max length.
  var LITERAL_CRLF = '\\r\\n';
  function toCRLF(value) {
    return String(value || '').replace(/\r?\n/g, LITERAL_CRLF);
  }
  // Back to real line breaks for the textarea (the attribute is rendered into it by Liquid).
  function fromCRLF(value) {
    return String(value || '').replace(/\\r\\n/g, '\n');
  }

  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/' + path;
  }

  function saveAttributes(attributes, keepalive) {
    return fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: !!keepalive,
    }).catch(function () {});
  }

  // Resubmits the cart form once the gift message is saved (second pass of the submit guard).
  function resubmit(form, submitter) {
    var button = submitter && submitter.form === form ? submitter : null;
    if (typeof form.requestSubmit === 'function') {
      try {
        button ? form.requestSubmit(button) : form.requestSubmit();
        return;
      } catch (err) {}
    }
    if (button && button.name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = button.name;
      hidden.value = button.value || '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  function strings() {
    var s = (window.theme && window.theme.strings && window.theme.strings.cart && window.theme.strings.cart.gift_message) || {};
    return {
      saved: s.saved || 'Votre message a bien été enregistré',
      forbidden: s.forbidden || 'Votre message contient un ou plusieurs caractères non autorisés : [chars]',
      tooLong: s.too_long || 'Vous avez dépassé la limite autorisée de [max] caractères',
      tooManyLines: s.too_many_lines || 'Votre message ne peut pas dépasser [max] lignes',
      counter: s.counter || '[count]/[max] caractères autorisés',
    };
  }

  /* ------------------------------------------------------------------ */
  /* Gift message                                                        */
  /* ------------------------------------------------------------------ */
  function initGiftMessage() {
    var root = $('[data-gift-message]');
    if (!root) return null;

    var checkbox = $('#gift_checkbox', root);
    var content = $('.gift-message-content', root);
    var textarea = $('[data-gift-message-input]', root);
    var flagInput = $('[data-gift-flag-attribute]', root);
    var giftKey = root.getAttribute('data-gift-attribute') || 'gift_message';
    var flagKey = root.getAttribute('data-gift-flag-attribute') || 'include_gift_message';
    var saveTimer = null;
    var cta = $('#gift-validation-cta', root);
    var errorEl = $('#rchars', root);
    var successEl = $('#validationchars', root);
    var countEl = $('#count', root);
    var maxLines = parseInt(root.getAttribute('data-gift-max-lines'), 10) || 5;
    var charsPerLine = parseInt(root.getAttribute('data-gift-chars-per-line'), 10) || 50;
    var maxChars = parseInt(root.getAttribute('data-gift-max-chars'), 10) || maxLines * charsPerLine;
    var textOutput = '';
    var onPaste = false;
    var messageTimer = null;
    var txt = strings();

    function isOpen() {
      return checkbox.checked;
    }

    function attributesPayload() {
      var attrs = {};
      attrs[giftKey] = isOpen() ? toCRLF(textarea.value) : '';
      attrs[flagKey] = isOpen() ? 'true' : 'false';
      return attrs;
    }

    // Saves the message (CR+LF line breaks) and the gift flag as cart attributes.
    function persist(keepalive) {
      clearTimeout(saveTimer);
      return saveAttributes(attributesPayload(), keepalive);
    }

    // Debounced save while typing, so a reload keeps the message.
    function syncValue() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(function () {
        persist(false);
      }, 400);
    }

    // The counter shows the length as it will be sent (CR+LF counts for 2 characters).
    function updateCount() {
      if (countEl) countEl.textContent = txt.counter.replace('[count]', String(toCRLF(textarea.value).length)).replace('[max]', String(maxChars));
    }

    function hideMessages() {
      if (errorEl) errorEl.style.display = 'none';
      if (successEl) successEl.style.display = 'none';
    }

    function flash(el, text) {
      hideMessages();
      if (!el) return;
      el.textContent = text;
      el.style.display = 'block';
      if (messageTimer) clearTimeout(messageTimer);
      messageTimer = setTimeout(hideMessages, 3000);
    }

    function setOpen(open, persistNow) {
      content.style.display = open ? 'block' : 'none';
      if (flagInput) flagInput.value = open ? 'true' : 'false';
      if (!open) {
        textarea.value = '';
        textOutput = '';
        textarea.classList.remove('validation-green-border');
        updateCount();
        hideMessages();
      }
      if (persistNow) persist(false);
    }

    // Reflow the text so that no line exceeds `charsPerLine` and no more than `maxLines`
    // lines are used (the card is printed line by line by the logistics partner).
    function formatTextAsRequired() {
      var textInput = textarea.value;
      var inputAsRows = textInput.split('\n');
      var cursorPositionOnInput = textarea.selectionStart;
      var cursorOffsetAfterOutput = 0;
      var visibleCharactersPerLine = Math.floor(textarea.offsetWidth / 9) || charsPerLine;
      var additionalTextAreaRows = 0;
      var totalRows = inputAsRows.length;
      var row;

      for (row = 0; row < totalRows; ++row) {
        if (inputAsRows[row].length > charsPerLine) {
          if (inputAsRows[row + 1] === undefined) {
            inputAsRows[row + 1] = '';
            totalRows++;
          }
          inputAsRows[row + 1] = inputAsRows[row].substring(charsPerLine) + inputAsRows[row + 1];
          inputAsRows[row] = inputAsRows[row].substring(0, charsPerLine);
          var newOutput = inputAsRows.join('\n');
          if (newOutput.substr(cursorPositionOnInput - 1, 1) === '\n') {
            cursorOffsetAfterOutput = 1;
          }
        }
        if (inputAsRows[row].length > visibleCharactersPerLine) {
          additionalTextAreaRows += Math.floor(inputAsRows[row].length / visibleCharactersPerLine);
        }
      }

      if (inputAsRows.length <= maxLines && toCRLF(inputAsRows.join('\n')).length <= maxChars) {
        textOutput = inputAsRows.join('\n');
        textarea.rows = inputAsRows.length + additionalTextAreaRows;
        hideMessages();
        if (onPaste) {
          cursorOffsetAfterOutput = cursorOffsetOnPaste(textInput, cursorPositionOnInput, totalRows);
        }
      } else {
        // too many lines or too long: keep the previous valid text
        flash(errorEl, inputAsRows.length > maxLines ? txt.tooManyLines.replace('[max]', String(maxLines)) : txt.tooLong.replace('[max]', String(maxChars)));
        cursorOffsetAfterOutput = -1;
      }

      textarea.value = textOutput;
      var pos = Math.max(0, cursorPositionOnInput + cursorOffsetAfterOutput);
      textarea.setSelectionRange(pos, pos);
      onPaste = false;
    }

    function countLineBreaks(string, lengthFromStart) {
      return string.substr(0, lengthFromStart).split('\n').length;
    }

    function cursorOffsetOnPaste(textInput, cursorPositionOnInput, totalRows) {
      var countOld = countLineBreaks(textInput, cursorPositionOnInput);
      var countNew = countLineBreaks(textOutput, cursorPositionOnInput + totalRows);
      return countNew - countOld;
    }

    function findForbiddenChars(str) {
      var found = [];
      for (var i = 0; i < str.length; i++) {
        var c = str.charAt(i);
        if (FORBIDDEN_CHARS.indexOf(c) !== -1 && found.indexOf(c) === -1) found.push(c);
      }
      return found;
    }

    function validate(showSuccess) {
      if (!isOpen()) return true;
      var value = textarea.value;
      var forbidden = findForbiddenChars(value);
      if (forbidden.length > 0) {
        flash(errorEl, txt.forbidden.replace('[chars]', forbidden.join(' ')));
        textarea.classList.remove('validation-green-border');
        return false;
      }
      if (value.split('\n').length > maxLines) {
        flash(errorEl, txt.tooManyLines.replace('[max]', String(maxLines)));
        textarea.classList.remove('validation-green-border');
        return false;
      }
      if (toCRLF(value).length > maxChars) {
        flash(errorEl, txt.tooLong.replace('[max]', String(maxChars)));
        textarea.classList.remove('validation-green-border');
        return false;
      }
      syncValue();
      textarea.classList.add('validation-green-border');
      if (showSuccess) flash(successEl, txt.saved);
      return true;
    }

    textarea.value = fromCRLF(textarea.value);
    textOutput = textarea.value;
    setOpen(isOpen(), false);
    updateCount();

    checkbox.addEventListener('change', function () {
      setOpen(isOpen(), true);
    });
    textarea.addEventListener('input', function () {
      this.value = this.value.replace(EMOJI_REGEX, '');
      formatTextAsRequired();
      updateCount();
      syncValue();
    });
    textarea.addEventListener('paste', function () {
      onPaste = true;
    });
    textarea.addEventListener('focus', function () {
      this.classList.remove('inactive');
      this.classList.add('active');
    });
    textarea.addEventListener('blur', function () {
      this.classList.remove('active');
      this.classList.add('inactive');
    });
    if (cta) {
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        validate(true);
      });
    }

    return {
      validate: function () {
        return validate(false);
      },
      beforeSubmit: function () {
        if (!isOpen()) {
          textarea.value = '';
          if (flagInput) flagInput.value = 'false';
        }
      },
      // Saves the message right before the form is submitted (keepalive survives the navigation).
      persist: function () {
        return persist(true);
      },
      scrollIntoView: function () {
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    };
  }

  /* ------------------------------------------------------------------ */
  /* Terms & conditions gate                                             */
  /* ------------------------------------------------------------------ */
  function initTermsGate(gift) {
    var form = document.getElementById('cartFormSubmit');
    var checkbox = $('[data-cart-terms-checkbox]');
    var attributeInput = $('[data-cart-terms-input]');
    var errorEl = $('.cart_uncheck_prompt');
    var errorText = $('.t-and-c-error');
    var wrapper = $('[data-cart-terms]');
    var submitWrapper = $('[data-cart-submit-wrapper]');
    if (!form || !checkbox) return;

    function submitButton() {
      return document.getElementById('cartSubmit');
    }

    function accepted() {
      return checkbox.checked;
    }

    function hideError() {
      if (errorEl) errorEl.classList.remove('is-visible');
      if (errorText) errorText.style.display = 'none';
      if (wrapper) wrapper.classList.remove('cart_checkbox--error');
    }

    function showError() {
      if (errorEl) errorEl.classList.add('is-visible');
      if (errorText) errorText.style.display = 'block';
      if (wrapper) {
        wrapper.classList.add('cart_checkbox--error');
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function sync() {
      var btn = submitButton();
      if (btn) btn.disabled = !accepted();
      if (attributeInput) attributeInput.value = accepted() ? 'true' : 'false';
      if (accepted()) hideError();
    }

    checkbox.addEventListener('change', sync);
    sync();

    if (submitWrapper) {
      submitWrapper.addEventListener('click', function () {
        if (!accepted()) showError();
      });
    }

    // Guard the native submission (capture phase: runs before the MinMaxify rules in custom-script.js).
    form.addEventListener(
      'submit',
      function (e) {
        var submitter = e.submitter || document.activeElement;
        var isCheckout = !submitter || submitter.id === 'cartSubmit' || submitter.name === 'checkout';
        if (!isCheckout) return;

        // Second pass: the gift message is saved, let custom-script.js (MinMaxify rules) and the browser proceed.
        if (form.dataset.giftSynced === '1') {
          delete form.dataset.giftSynced;
          if (attributeInput) attributeInput.value = 'true';
          return;
        }

        if (!accepted()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError();
          return false;
        }
        if (attributeInput) attributeInput.value = 'true';
        if (gift) {
          gift.beforeSubmit();
          if (!gift.validate()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            gift.scrollIntoView();
            return false;
          }
          // The message goes through the Ajax API (keeps CR+LF): save it first, then resubmit.
          e.preventDefault();
          e.stopImmediatePropagation();
          gift.persist().then(
            function () {},
            function () {}
          ).then(function () {
            form.dataset.giftSynced = '1';
            resubmit(form, submitter);
          });
          return false;
        }
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */
  /* Cart note counter (unchanged behaviour)                             */
  /* ------------------------------------------------------------------ */
  function initCartNote() {
    var note = document.querySelector('#CartSpecialInstructions');
    var count = document.querySelector('#note-count');
    var countText = '/70 caractères autorisés';
    if (!note) return;
    if (count) count.innerHTML = note.value.length + countText;
    note.addEventListener('input', function () {
      this.value = this.value.replace(/(?:[`~<>;:"\/\[\]\|{}()=_+-])|[!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|]/g, '').replace(EMOJI_REGEX, '');
      if (count) count.innerHTML = this.value.length + countText;
    });
  }

  function init() {
    var gift = initGiftMessage();
    initTermsGate(gift);
    initCartNote();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------ */
/* Quantity +/- on the cart page                                       */
/*                                                                      */
/* Pre-existing bug fixed here: Shella's CartController (theme.js) was  */
/* also listening on these buttons and posted the *previous* quantity   */
/* to /cart/change.js, while this handler posted the whole form to      */
/* /checkout. The cart ended up out of sync with what the page showed.  */
/* Now this handler is the only one (stopPropagation), it updates the   */
/* line through /cart/change.js, keeps the excise/vignette attributes   */
/* in sync and refreshes the prices in place (no reload, so the gift   */
/* message and the terms checkbox are kept).                            */
/* ------------------------------------------------------------------ */
$(".cart__items .product-quantity-info").on('click', '.js-cart-product-quantity [data-control]', async function(e){
  e.preventDefault();
  e.stopPropagation();
  let $this = $(this);
  let quantity_parent_selector = $this.parents(".js-cart-product-quantity");
  let input = quantity_parent_selector.find("input");
  let line = parseInt(quantity_parent_selector.attr('data-product-cart-line'), 10);
  let sku = quantity_parent_selector.attr('data-sku');
  let accise_tax = quantity_parent_selector.attr('data-accise-tax');
  let vignette_tax = quantity_parent_selector.attr('data-vignette-tax');
  let product_id = quantity_parent_selector.attr('data-product-id');
  let variant_title = quantity_parent_selector.attr('data-variant-title');
  let direction = $this.attr('data-control');
  let quantity = parseInt(input.val(), 10);
  if (!$.isNumeric(quantity) || quantity < 1 || !line) return false;
  if (quantity_parent_selector.data('busy')) return false;

  let current_val = direction === '+' ? quantity + 1 : quantity - 1;
  if (current_val > 0 && typeof addToCartRules === 'function') {
    const rules = await addToCartRules(product_id, null, variant_title, current_val);
    if (rules) { showRulePopup(rules); return false; }
  }

  quantity_parent_selector.data('busy', true);
  input.val(current_val);
  updateTaxes(current_val, sku, accise_tax, vignette_tax);

  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: current_val })
    });
    if (!response.ok) throw new Error('cart change failed');
    const cart = await response.json();
    if (current_val < 1 || cart.item_count === 0) {
      location.reload();
      return false;
    }
    refreshCartPrices(cart);
    // Shella keeps its own cart state (header bubble, popup) in sync through this event.
    document.dispatchEvent(new Event('cart:updated'));
  } catch (err) {
    location.reload();
  } finally {
    quantity_parent_selector.data('busy', false);
  }
  return false;
});

function refreshCartPrices(cart) {
  const format = (amount) => (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme && theme.moneyFormat)
    ? Shopify.formatMoney(amount, theme.moneyFormat)
    : (amount / 100).toFixed(2).replace('.', ',') + ' €';
  cart.items.forEach((item, index) => {
    const lineNumber = index + 1;
    const $row = $('[data-product-cart-line="' + lineNumber + '"]').closest('.gift-container-row');
    $row.find('input.js-product-quantity-from-cart').val(item.quantity);
    $row.find('.item-cart-price .price, .cart_mobile_qty .price').each(function () {
      const $price = $(this);
      ($price.children().length ? $price.children().last() : $price).text(format(item.line_price));
    });
  });
  $('.cart__subtotal h3').text(format(cart.total_price));
  $('.total-cart-items-val').val(cart.item_count);
}

function updateTaxes(quantity, sku, acciseTax, vignetteTax){
  // Keep the hidden excise / vignette attributes in sync with the new quantity. They are
  // sent with the native cart form when the customer proceeds to checkout.
  let accise = acciseTax ? acciseTax : 0;
  let vignette = vignetteTax ? vignetteTax : 0;
  let acciseTaxeHiddenInput = $("#accise_tax").val() || '[]';
  let vignetteTaxHiddenInput = $("#vignette_tax").val() || '[]';

  // convert string to correct javascript json format ex: [{sku: 1234, value: 4.5}] to [{"sku": 1234, "value": 4.5}]
  let acciseTaxObject = JSON.parse(acciseTaxeHiddenInput.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": '));
  let vignetteTaxObject = JSON.parse(vignetteTaxHiddenInput.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": '));

  let acciseTaxeIndex = acciseTaxObject.findIndex((obj => obj.sku == sku));
  let vignetteTaxIndex = vignetteTaxObject.findIndex((obj => obj.sku == sku));

  if(acciseTaxObject[acciseTaxeIndex]) acciseTaxObject[acciseTaxeIndex].value = (1 * accise * quantity).toFixed(2) * 1;
  if(vignetteTaxObject[vignetteTaxIndex]) vignetteTaxObject[vignetteTaxIndex].value = (1 * vignette * quantity).toFixed(2) * 1;

  // Before set data on input value we formatting data ex: [{"sku": 1234, "value": 4.5}] to [{sku: 1234, value: 4.5}]
  let acciseTaxStringObject = JSON.stringify(acciseTaxObject).replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '$2:');
  let vignetteTaxStringObject = JSON.stringify(vignetteTaxObject).replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '$2:');
  $("#accise_tax").val(acciseTaxStringObject);
  $("#vignette_tax").val(vignetteTaxStringObject);
}
